import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import { dashboardAPI } from '../utils/api';
import { FileText, Users, DollarSign, PlusCircle } from 'lucide-react';
import './WorkPage.css';

const WorkPage = () => {
  const navigate = useNavigate();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [stats, setStats] = useState({
    pendingLoans: 0,
    disbursedLoans: 0,
    totalLoans: 0,
    commission: 0,
    newCustomers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardAPI.getStats();
      const apiStats = response.data.stats || response.data;
      setStats({
        pendingLoans: apiStats.pendingLoans || 0,
        disbursedLoans: apiStats.disbursedLoans || 0,
        totalLoans: apiStats.totalLoans || 0,
        commission: apiStats.currentMonthCommission || 0,
        newCustomers: apiStats.newCustomers || 0,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const workItems = [
    {
      id: 'new-loan',
      title: t('createNewLoan'),
      description: t('createNewLoanDesc'),
      icon: <PlusCircle size={32} />,
      color: 'primary',
      path: '/loan/new',
    },
    {
      id: 'loan-list',
      title: t('loanList'),
      description: t('loanListDesc'),
      icon: <FileText size={32} />,
      color: 'info',
      path: '/loan/list',
    },
    {
      id: 'customers',
      title: t('customers'),
      description: t('customersDesc'),
      icon: <Users size={32} />,
      color: 'success',
      path: '/loan/customers',
    },
    {
      id: 'commission',
      title: t('commission'),
      description: t('commissionDesc'),
      icon: <DollarSign size={32} />,
      color: 'warning',
      path: '/loan/commission',
    },
  ];

  return (
    <div className="work-page">
      <div className="page-header">
        <h1 className="page-title">{t('work')}</h1>
        <p className="page-subtitle">{t('loanService')}</p>
      </div>

      <div className="page-content">
        <div className="work-grid">
          {workItems.map((item) => (
            <div
              key={item.id}
              className={`work-card ${item.color}`}
              onClick={() => navigate(item.path)}
            >
              <div className="work-icon">{item.icon}</div>
              <h3 className="work-title">{item.title}</h3>
              <p className="work-description">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <h2 className="section-title">{t('quickOverview')}</h2>
          <div className="stats-row">
            <div className="stat-box">
              <div className="stat-number">{loading ? '...' : stats.totalLoans}</div>
              <div className="stat-label">{t('draftLoans')}</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{loading ? '...' : stats.pendingLoans}</div>
              <div className="stat-label">{t('pendingApproval')}</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{loading ? '...' : stats.disbursedLoans}</div>
              <div className="stat-label">{t('disbursedLoans')}</div>
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="recent-activities">
          <h2 className="section-title">{t('recentActivity')}</h2>

          <div className="activity-list">
            <div className="activity-card">
              <div className="activity-info">
                <h3 className="activity-title">{t('commissionThisMonth')}</h3>
                <p className="activity-desc">
                  {stats.commission?.toLocaleString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US')} {language === 'ko' ? 'KRW' : 'VND'}
                </p>
              </div>
              <div className="activity-meta">
                <span className="activity-badge primary">{t('commissionLabel')}</span>
                <p className="activity-date">{new Date().toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US')}</p>
              </div>
            </div>

            <div className="activity-card">
              <div className="activity-info">
                <h3 className="activity-title">{t('newCustomers')}</h3>
                <p className="activity-desc">{t('newCustomersCount')}: {stats.newCustomers || 0}</p>
              </div>
              <div className="activity-meta">
                <span className="activity-badge success">{t('customersLabel')}</span>
                <p className="activity-date">{new Date().toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkPage;
